import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { verifyPayment } from '@/api/payments/paymentsService'
import { TranzilaHostedFields } from '@/components/TranzilaHostedFields'
import { Card, CardContent } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { useToast } from '@/hooks/useToast'
import { RoutePath } from '@/RoutePath'

import { PaymentLinkResponse } from './components/PaymentData'

const VERIFY_POLL_INTERVAL_MS = 2000
const VERIFY_MAX_ATTEMPTS = 8

interface Props {
  paymentLinkData: PaymentLinkResponse | null
  pkgId: string
  onCompletePaymentSuccess: () => void
}

export default function ProcessPage({ paymentLinkData, pkgId, onCompletePaymentSuccess }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { showErrorToast } = useToast()
  const [isVerifying, setIsVerifying] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const chargePriceRef = useRef<string | null>(null)

  const startVerifyPolling = useCallback(() => {
    setIsVerifying(true)
    let attempts = 0

    const poll = async () => {
      attempts++
      try {
        const { isPaid } = await verifyPayment(pkgId)
        if (isPaid) {
          if (pollRef.current) clearInterval(pollRef.current)
          onCompletePaymentSuccess()
          return
        }
      } catch {
        // keep polling
      }
      if (attempts >= VERIFY_MAX_ATTEMPTS) {
        console.info({
          message: 'Payment verification failed',
          attempts,
          maxAttempts: VERIFY_MAX_ATTEMPTS,
          pollRef: pollRef.current,
          navigate,
        })
        if (pollRef.current) clearInterval(pollRef.current)
        onCompletePaymentSuccess()
        navigate(RoutePath.ClientPkg)
      }
    }

    poll()
    pollRef.current = setInterval(poll, VERIFY_POLL_INTERVAL_MS)
  }, [pkgId, onCompletePaymentSuccess, navigate])

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [])

  useEffect(() => {
    if (!paymentLinkData) {
      navigate(`${RoutePath.PaymentSummary}/${pkgId}`)
    }
  }, [paymentLinkData, pkgId, navigate])

  const handleChargeSuccess = useCallback(
    (response: TranzilaChargeResponse) => {
      chargePriceRef.current = response.transaction_response?.amount ?? null
      startVerifyPolling()
    },
    [startVerifyPolling]
  )

  const handleChargeError = useCallback(
    (error: string) => {
      showErrorToast(t('payment.chargeError'), error)
    },
    [showErrorToast, t]
  )

  if (!paymentLinkData) return null

  return (
    <div className='space-y-4'>
      {!isVerifying && (
        <TranzilaHostedFields paymentLinkData={paymentLinkData} onSuccess={handleChargeSuccess} onError={handleChargeError} />
      )}

      {isVerifying && (
        <Card>
          <CardContent className='flex flex-col items-center gap-6 py-12'>
            <Spinner className='size-8' />
            <p className='text-text-primary text-sm font-medium'>{t('payment.verifyingPayment')}</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
